/**
 * Sprint 247 — Platform Activity panel
 *
 * Lives inside the KRO Platform Admin dashboard (route /admin).
 * Read-only operational view of live platform activity. Designed to grow:
 * today it shows rate-limit activity, but the layout is intentionally
 * extensible so new operational metrics (webhook health, scheduler lag,
 * etc.) can land below without a redesign.
 *
 * Primary focus per platform-team brief: surface companies that are
 * APPROACHING their rate limits, with company name + limiter + usage + %.
 * Global per-limit summary is secondary context, shown as a compact strip.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, RefreshCw, Server, Clock, Calendar, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const u = getAuth().currentUser;
  if (!u) throw new Error('Not authenticated');
  return u.getIdToken();
}

const fmtTimeAgo = (iso) => {
  if (!iso) return '—';
  try {
    const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 5) return 'just now';
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
    return new Date(iso).toLocaleTimeString();
  } catch { return iso; }
};

const fmtWindow = (s) => {
  if (s <= 60) return 'per min';
  if (s <= 3600) return 'per hour';
  return 'per day';
};

const fmtNextRun = (iso) => {
  if (!iso) return '—';
  try {
    const ms = new Date(iso).getTime() - Date.now();
    const secs = Math.round(ms / 1000);
    if (Math.abs(secs) < 60) return `in ${Math.max(0, secs)}s`;
    const mins = Math.round(secs / 60);
    if (Math.abs(mins) < 90) return `in ${mins} min`;
    const hrs = Math.round(secs / 3600);
    if (Math.abs(hrs) < 36) return `in ${hrs} hr`;
    return new Date(iso).toLocaleString();
  } catch { return iso; }
};

const fmtInterval = (s) => {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} hr`;
  return `${Math.round(s / 86400)} day`;
};

// ── Companies Near Limits — primary focus ────────────────────────────────────

const PctBadge = ({ pct }) => {
  let cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (pct >= 90) cls = 'bg-red-50 text-red-700 border-red-200';
  else if (pct >= 75) cls = 'bg-amber-50 text-amber-700 border-amber-200';
  else if (pct >= 50) cls = 'bg-yellow-50 text-yellow-700 border-yellow-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}
          data-testid={`pct-badge-${Math.round(pct)}`}>
      {pct.toFixed(1)}%
    </span>
  );
};

const CompaniesNearLimits = ({ rows, loading }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
       data-testid="platform-activity-near-limits">
    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
      <AlertTriangle className="w-4 h-4 text-amber-500" />
      <h3 className="text-sm font-semibold text-gray-800">Companies Near Limits</h3>
      <span className="ml-auto text-xs text-gray-400">
        showing tenants at ≥ 50% of any limit (top 10)
      </span>
    </div>
    {loading && !rows ? (
      <div className="p-8 text-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-300 mx-auto" />
        <p className="text-sm text-gray-400 mt-2">Loading…</p>
      </div>
    ) : !rows || rows.length === 0 ? (
      <div className="p-6 text-center text-sm text-gray-400" data-testid="platform-activity-near-empty">
        Nothing close to a limit right now. ✅
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Limiter</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Usage</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">% of Limit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map((r, i) => (
              <tr key={`${r.companyId}-${r.limitName}-${i}`}
                  className="hover:bg-gray-50 transition-colors"
                  data-testid={`near-limit-row-${r.companyId}-${r.limitName}`}>
                <td className="px-5 py-3">
                  <div className="font-medium text-gray-900 truncate max-w-[280px]">{r.companyName}</div>
                  <div className="text-xs text-gray-400 font-mono truncate max-w-[280px]">{r.companyId}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                                   bg-indigo-50 text-indigo-700 border border-indigo-200">
                    {r.limitName}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                  {r.current} / {r.threshold}
                </td>
                <td className="px-4 py-3 text-right">
                  <PctBadge pct={r.pctFull} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ── Per-Limit Summary Strip — compact, secondary context ─────────────────────

const LimitSummaryStrip = ({ limits }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4"
       data-testid="platform-activity-limits-strip">
    <div className="flex items-center gap-2 mb-3">
      <Activity className="w-4 h-4 text-indigo-500" />
      <h3 className="text-sm font-semibold text-gray-800">Platform-Wide Limit Summary</h3>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
      {(limits || []).map((l) => (
        <div key={l.name}
             className="border border-gray-100 rounded-lg px-3 py-2 hover:border-indigo-200 transition-colors"
             data-testid={`limit-summary-${l.name}`}>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-xs font-semibold text-gray-700 capitalize">{l.name}</span>
            {l.rejectionsLastHour > 0 && (
              <span className="text-[10px] font-semibold text-red-600
                               bg-red-50 px-1.5 py-0.5 rounded-full border border-red-200"
                    data-testid={`limit-rejections-${l.name}`}>
                {l.rejectionsLastHour} × 429 (1h)
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            <span className="tabular-nums">{l.threshold}</span>{' '}
            <span className="text-gray-400">{fmtWindow(l.windowSecs)}</span>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {l.tenantsActive === 0
              ? 'no active tenants'
              : `${l.tenantsActive} tenant${l.tenantsActive === 1 ? '' : 's'} active`}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── Scheduler Health — recurring background jobs ─────────────────────────────

const STATUS_STYLE = {
  Healthy: { icon: CheckCircle2, color: 'text-emerald-600',
             pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  Delayed: { icon: Clock,        color: 'text-amber-600',
             pill: 'bg-amber-50 text-amber-700 border-amber-200' },
  Stale:   { icon: AlertCircle,  color: 'text-red-600',
             pill: 'bg-red-50 text-red-700 border-red-200' },
  Error:   { icon: XCircle,      color: 'text-red-600',
             pill: 'bg-red-50 text-red-700 border-red-200' },
};

const StatusPill = ({ status }) => {
  const style = STATUS_STYLE[status] || STATUS_STYLE.Healthy;
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${style.pill}`}
          data-testid={`scheduler-status-${status.toLowerCase()}`}>
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
};

const SchedulerHealth = ({ health, loading }) => {
  const jobs = health?.jobs || [];
  const summary = health?.summary || {};
  const unhealthyCount = (summary.delayed || 0) + (summary.stale || 0) + (summary.error || 0);
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
         data-testid="scheduler-health-section">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-indigo-500" />
        <h3 className="text-sm font-semibold text-gray-800">Scheduler Health</h3>
        <span className="ml-auto flex items-center gap-2 text-xs">
          {unhealthyCount > 0 ? (
            <span className="inline-flex items-center gap-1 text-red-600 font-semibold"
                  data-testid="scheduler-health-unhealthy-count">
              <AlertCircle className="w-3.5 h-3.5" />
              {unhealthyCount} need{unhealthyCount === 1 ? 's' : ''} attention
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"
                  data-testid="scheduler-health-all-healthy">
              <CheckCircle2 className="w-3.5 h-3.5" />
              All {jobs.length || 0} jobs healthy
            </span>
          )}
        </span>
      </div>
      {loading && !health ? (
        <div className="p-8 text-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-300 mx-auto" />
          <p className="text-sm text-gray-400 mt-2">Loading…</p>
        </div>
      ) : jobs.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-400">
          No scheduled jobs registered.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Job</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Success</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Next Run</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Interval</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {jobs.map((j) => {
                const isUnhealthy = j.status !== 'Healthy';
                return (
                  <tr key={j.jobId}
                      className={`hover:bg-gray-50 transition-colors ${isUnhealthy ? 'bg-amber-50/30' : ''}`}
                      data-testid={`scheduler-row-${j.jobId}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-900 truncate">{j.label}</div>
                      <div className="text-xs text-gray-400 font-mono truncate">{j.jobId}</div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={j.status} />
                      {j.note && (
                        <div className="text-xs text-gray-500 mt-1 max-w-[280px] truncate"
                             title={j.note}>{j.note}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 tabular-nums">
                      {j.lastSuccessAt ? fmtTimeAgo(j.lastSuccessAt) : <span className="text-gray-400">never</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 tabular-nums">
                      {fmtNextRun(j.nextRunAt)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      every {fmtInterval(j.expectedIntervalSecs)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Public component ──────────────────────────────────────────────────────────

export const PlatformActivity = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tick, setTick]       = useState(0);   // forces "last updated" re-render

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res = await axios.get(`${API}/api/admin/platform-activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load platform activity';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => { fetchData(); }, [fetchData]);

  // "Last updated" auto-refresh string every 5s (no extra requests).
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="space-y-4 mb-8" data-testid="platform-activity-section">
      {/* Header: title + Last Updated + Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-gray-800">Platform Activity</h2>
          <span className="text-xs text-gray-400">Live, this pod</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {data?.podId && (
            <span className="inline-flex items-center gap-1 font-mono" data-testid="platform-activity-pod">
              <Server className="w-3.5 h-3.5" /> {data.podId}
            </span>
          )}
          {data?.asOf && (
            <span className="inline-flex items-center gap-1" data-testid="platform-activity-updated">
              <Clock className="w-3.5 h-3.5" />
              Updated {fmtTimeAgo(data.asOf)}
              {/* `tick` forces this string to re-render every 5s */}
              <span className="hidden">{tick}</span>
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="platform-activity-refresh-btn"
            className="gap-1.5 h-7"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm
                        flex items-center gap-2" data-testid="platform-activity-error">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* PRIMARY: Companies near limits */}
      <CompaniesNearLimits
        rows={data?.rateLimits?.companiesNearLimits}
        loading={loading}
      />

      {/* SECONDARY: Per-limit summary strip */}
      <LimitSummaryStrip limits={data?.rateLimits?.limits} />

      {/* SECONDARY: Scheduler health */}
      <SchedulerHealth health={data?.schedulerHealth} loading={loading} />
    </section>
  );
};

export default PlatformActivity;
